import { FormattedMessage } from 'react-intl';
import { IconLibrary } from '@product-framework/index';
import { useState } from 'react';
import {
  SnackbarApiErrorMessage,
  UnSavedChangesDialog,
  useDataProvider,
  useDialog,
  useLocalization,
  useSnackbar,
} from '@ui-framework/index';
import { Box, FormHelperText, Typography } from '@mui/material';
import PermissionButton from '../PermissionButton';

function DialogContent({ firstName, lastName }) {
  return (
    <Box>
      <Typography>
        <FormattedMessage
          id="DO_YOU_WANT_TO_RESET_MULTI_FACTOR_AUTHENTICATION_FOR"
          defaultMessage="Do you want to reset multi-factor authentication for"
        />{' '}
        <Typography component="span" color="primary.main">
          {firstName} {lastName}
        </Typography>
        ?
      </Typography>
      <FormHelperText>
        <FormattedMessage
          id="MFA_SPECIAL_INSTRUCTION"
          defaultMessage={
            'If you click "Yes", the user\'s MFA devices will been removed. The user will be required to set up multi-factor authentication the next time they sign in.'
          }
        />
      </FormHelperText>
    </Box>
  );
}

function UserResetMFADialogWithButton({
  email,
  firstName,
  lastName,
  permissionName = null,
  formRef = null,
}) {
  if (!permissionName) {
    throw new Error(
      'Please provide permission name for UserResetMFADialogWithButton',
    );
  }
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { post, getApiUrl } = useDataProvider(provider);
  const snackbar = useSnackbar();
  const { confirmationRef } = useDialog();
  const { formatMessage } = useLocalization();
  const [isDirty, setIsDirty] = useState(false);

  const resetMFA = async () => {
    const url = `${getApiUrl()}/sxcl/v1/auth/reset-multi-factor-authentication`;
    const response = await post(url, {
      email,
    });
    if (response?.ok) {
      return response;
    }
    snackbar.showMessage({
      type: 'error',
      data: <SnackbarApiErrorMessage errors={response?.errors} />,
    });
    return null;
  };

  const resetMFAClick = () => {
    confirmationRef.current.init({
      Icon: <IconLibrary.MFA />,
      message: <DialogContent firstName={firstName} lastName={lastName} />,
      positiveButtonIcon: <IconLibrary.Yes />,
      negativeButtonIcon: <IconLibrary.Cancel />,
      positiveButtonText: 'Yes',
      negativeButtonText: 'Cancel',
      width: 360,
      title: 'Reset MFA',
      onChange: async isYes => {
        if (isYes) {
          const response = await resetMFA();
          if (response?.ok) {
            snackbar.showMessage(
              formatMessage({
                id: 'USER_MFA_WAS_RESET_SUCCESSFULLY',
                defaultMessage: 'User MFA was reset successfully',
              }),
            );
          }
        }
        confirmationRef.current.setOpen(false);
      },
    });
    confirmationRef.current.setOpen(true);
  };

  return (
    <>
      <PermissionButton
        permissionName={permissionName}
        variant="contained"
        color="primary"
        startIcon={<IconLibrary.MFA />}
        onClick={() => {
          if (formRef?.current && formRef?.current?.isDirty()) {
            setIsDirty(true);
          } else {
            resetMFAClick();
          }
        }}>
        {formatMessage({
          id: 'RESET_MFA',
          defaultMessage: 'Reset MFA',
        })}
      </PermissionButton>
      <UnSavedChangesDialog
        open={isDirty}
        onStay={() => setIsDirty(false)}
        onLeave={() => {
          setIsDirty(false);
          formRef?.current?.reset();
          resetMFAClick();
        }}
      />
    </>
  );
}

export default UserResetMFADialogWithButton;
